import React, { useEffect, useRef } from "react";

// Copy per alert (CU-86cbawn2a). Late is the heavier case, so it states the cost
// will be charged; post-cutoff says a cost may apply. Neither shows an amount.
export const COST_ALERT_COPY = {
  late: {
    title: "Cancel trip? Cost still applies",
    body: "Cancelling this trip too late means the trip cost will still be charged. Are you sure?",
  },
  post_cutoff: {
    title: "Cancel trip? A cost may apply",
    body: "The cut-off time for this trip has passed, so cancelling it now may still incur a cost. Are you sure?",
  },
};

/**
 * Blocking warning shown before a cancellation that may cost money. Shared by
 * every web cancel flow so the alert reads the same everywhere.
 *
 * @param {string} alert 'late' or 'post_cutoff'
 * @param {function} onConfirm proceeds with the cancellation
 * @param {function} onDismiss closes the modal with nothing changed
 * @param {boolean} loading disables both actions while the cancel is in flight
 * @param {JSX.Element} children optional extra content under the warning, e.g. a batch summary
 * Styling is self-contained: host pages such as Team Manager set white-space:
 * nowrap, so the card resets it, and only Tailwind 1 utilities are used.
 *
 * @returns {JSX.Element|null} the modal, or null when there is no alert
 */
const CostAlertModal = ({ alert, onConfirm, onDismiss, loading = false, children }) => {
  const copy = COST_ALERT_COPY[alert];
  const dismissRef = useRef(null);

  // Start on the safe choice, so Enter keeps the trip rather than cancelling it
  useEffect(() => {
    if (copy) dismissRef.current?.focus();
  }, [copy]);

  useEffect(() => {
    if (!copy) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !loading) onDismiss();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [copy, loading, onDismiss]);

  if (!copy) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" data-testid="cost-alert-modal" data-alert={alert}>
      <button
        type="button"
        tabIndex={-1}
        aria-label="Dismiss"
        data-testid="cost-alert-backdrop"
        className="absolute inset-0 w-full h-full bg-gray-800 bg-opacity-50 cursor-default focus:outline-none"
        onClick={() => !loading && onDismiss()}
      />
      <div
        className="relative bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4 whitespace-normal break-words"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cost-alert-title"
        aria-describedby="cost-alert-body"
      >
        <button
          type="button"
          onClick={onDismiss}
          disabled={loading}
          aria-label="Close"
          data-testid="cost-alert-close"
          className="absolute top-0 right-0 mt-3 mr-3 w-8 h-8 rounded-full border border-gray-400 text-gray-600 hover:bg-gray-100 focus:outline-none"
        >
          &times;
        </button>
        <h3 id="cost-alert-title" className="text-lg font-medium text-gray-800 mb-2 pr-8" data-testid="cost-alert-title">
          {copy.title}
        </h3>
        <p id="cost-alert-body" className="text-gray-600 mb-4" data-testid="cost-alert-body">
          {copy.body}
        </p>
        {children}
        <div className="flex justify-end w-full space-x-4 mt-4">
          <button
            type="button"
            onClick={onDismiss}
            disabled={loading}
            ref={dismissRef}
            data-testid="cost-alert-dismiss"
            className="px-6 py-2 rounded text-white bg-gray-400 hover:bg-gray-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Keep trip
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            data-testid="cost-alert-confirm"
            className="px-6 py-2 rounded text-white bg-red-600 hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Cancelling..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CostAlertModal;
